import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/react";

import { Form } from "@Rolec-Services/rolec-ui";
import { useEffect, useReducer } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { router } from "~/router";
import { onboardingSchema, OnboardingType } from "~/schemas/forms/onboarding";
import { formReducer, getDefaultValues, initialFormState } from "~/utils/data";
import { numSteps, steps } from "~/utils/steps";
import { tryCatch } from "~/utils/try-catch";
import { ContentCard } from "../atoms/content-card";
import { Actions } from "../molecules/actions";
import { RenderStep } from "./render-step";

type FieldName = keyof OnboardingType;

export function ContentResolver() {
  const [formState, dispatch] = useReducer(formReducer, initialFormState);
  const { t } = useTranslation();
  const schema = onboardingSchema(t);

  const form = useForm<OnboardingType>({
    resolver: zodResolver(schema),
    defaultValues: getDefaultValues(),
  });

  // Update session storage & sentry when form data changes
  useEffect(() => {
    sessionStorage.setItem("step", formState.step.toString());
    sessionStorage.setItem("formData", JSON.stringify(form.getValues()));

    Sentry.setUser({
      email: form.getValues("team.owner.email"),
      geo: {
        country_code: form.getValues("company.address.country"),
        city: form.getValues("company.address.city"),
      },
      username: form.getValues("team.owner.name"),
    });
  }, [form, formState.step]);

  async function submit(data: OnboardingType) {
    const response = await fetch("/api/onboard", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        provider: "MONTA",
        payload: data,
      }),
    });

    if (!response.ok) {
      dispatch({ type: "SET_STATE", payload: "failed" });
      return;
    }

    const responseData = await response.json();
    const teamUrl = responseData.team.url;

    dispatch({ type: "SET_STATE", payload: "successful" });
    dispatch({ type: "SET_TEAM_URL", payload: teamUrl });
    sessionStorage.removeItem("formData");
    // sessionStorage.removeItem("step");
    form.reset();
  }

  const processForm: SubmitHandler<OnboardingType> = async (data) => {
    dispatch({ type: "SET_STATE", payload: "submitted" });

    const result = await tryCatch(submit(data));

    if (result.error) {
      console.error("Error:", result.error);
      dispatch({ type: "SET_STATE", payload: "failed" });
    }
  };

  async function nextStep() {
    if (formState.state === "successful" || formState.state === "submitted")
      return;

    // const tier = getTier();
    // const numFormSteps = tier === "sponsored" ? numSponsoredSteps : numSteps;

    const numFormSteps = numSteps;

    if (formState.step === numFormSteps) return;

    // const fields =
    //   tier === "sponsored"
    //     ? sponsoredSteps[formState.step].fields
    //     : steps[formState.step].fields;

    const fields = steps[formState.step].fields;

    // Trigger validation for the current step
    if (fields?.length) {
      const output = await form.trigger(fields as FieldName[], {
        shouldFocus: true,
      });
      if (!output) return;
    }

    // Custom validation for chargepoint array
    if (formState.step === 2) {
      const chargepoint = form.getValues("chargepoint");
      if (!chargepoint.length || chargepoint[0].iccidWithLuhn === "") return;
    }

    // Move to the next step if the current step is valid
    if (formState.step < numFormSteps) {
      // Submit the form if it's the last step
      if (formState.step === numFormSteps - 1) {
        form.handleSubmit(processForm)();
        console.log(form.formState.errors);
        return;
      }

      // Clear errors and move to the next step
      form.clearErrors();
      dispatch({ type: "SET_STEP", payload: formState.step + 1 });
      return;
    }
  }

  function prevStep() {
    if (formState.step === 0) {
      sessionStorage.removeItem("step");
      sessionStorage.removeItem("formData");
      router.navigate("/");
    }

    dispatch({ type: "SET_STEP", payload: formState.step - 1 });
  }

  function showActions() {
    return !formState.state || formState.state === "failed";
  }

  // function getTier() {
  //   return form.getValues("tier");
  // }

  function getNumSteps() {
    // const tier = getTier();
    // return tier === "sponsored" ? numSponsoredSteps : numSteps;
    return numSteps;
  }

  return (
    <ContentCard>
      <div className="flex flex-col justify-between h-full gap-4">
        <Form {...form}>
          <form className="flex flex-col gap-4">
            <RenderStep formState={formState} nextStep={nextStep} form={form} />
          </form>
        </Form>
        {showActions() && (
          <Actions
            step={formState.step}
            nextStep={nextStep}
            prevStep={prevStep}
            numSteps={getNumSteps()}
          />
        )}
      </div>
    </ContentCard>
  );
}
